import { useEffect, useRef, useState } from "react";
import { X, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { profileGalleryApi, streamProfileMedia } from "@/api/profileGallery";

// Profile Gallery replaces the retired Mist Library as the user's private store.
// This picker reads profile_media ({images, videos}) and always hands back a real
// File (fetched from profile-gallery), so existing device-upload handlers work
// unchanged — pass onFile={(f) => existingHandler({ target: { files: [f] } })}.
// Audio deliberately has NO Profile Gallery source (spec) — don't use it there.

function Thumb({ item, isVideo, onPick }) {
  const [src, setSrc] = useState(null);
  const [near, setNear] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: "300px" });
    io.observe(el); return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!near) return; let u; let dead = false;
    streamProfileMedia(item.id).then((x) => { if (dead) { URL.revokeObjectURL(x); return; } u = x; setSrc(x); }).catch(() => {});
    return () => { dead = true; if (u) URL.revokeObjectURL(u); };
  }, [near, item.id]);
  return (
    <button ref={ref} onClick={() => onPick(item)} data-testid={`pg-item-${item.id}`}
      className="relative aspect-square border-2 border-black bg-charcoal overflow-hidden active:translate-y-[1px]">
      {src
        ? (isVideo ? <video src={src} muted playsInline className="w-full h-full object-cover" /> : <img src={src} alt={item.filename || "asset"} className="w-full h-full object-cover" />)
        : <div className="grid h-full w-full place-items-center"><Loader2 size={16} className="animate-spin text-white/40" /></div>}
      {isVideo && <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-[#00F5D4] text-[7px] font-mono font-black px-1 py-0.5 uppercase">▶ vid</span>}
    </button>
  );
}

function PickerSheet({ bucket, title, onClose, onPicked }) {
  const [media, setMedia] = useState(null);
  const isVideo = bucket === "videos";
  useEffect(() => {
    profileGalleryApi.media(0)
      .then((d) => setMedia(isVideo ? (d.videos || []) : (d.images || [])))
      .catch(() => setMedia([]));
  }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 z-[300] bg-black/80 flex items-end justify-center" data-testid="pg-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0d0d0d] border-t-4 border-[#00F5D4] rounded-t-3xl p-4 max-h-[80dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 pb-3 mb-3 border-b-2 border-[#00F5D4]/20">
          <h3 className="flex-1 font-serif text-xl text-white">{title}</h3>
          <button onClick={onClose} data-testid="pg-close" className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] text-[#00F5D4]"><X size={15} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          {media == null ? <div className="grid place-items-center py-10"><Loader2 size={20} className="text-[#00F5D4] animate-spin" /></div>
            : media.length === 0 ? <p className="font-mono text-[10px] text-white/50 py-8 text-center">Nothing in your Profile Gallery yet — save a creation there first.</p>
              : <div className="grid grid-cols-3 gap-2">{media.map((it) => <Thumb key={it.id} item={it} isVideo={isVideo} onPick={onPicked} />)}</div>}
        </div>
      </div>
    </div>
  );
}

export default function ProfileGalleryPickButton({
  onFile, bucket = "images", label = "Profile Gallery",
  title = "Your Profile Gallery", className, testid = "profile-gallery-pick", disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const pick = async (item) => {
    setOpen(false); setLoading(true);
    try {
      const url = await streamProfileMedia(item.id);
      const blob = await (await fetch(url)).blob();
      const ext = bucket === "videos" ? "mp4" : "png";
      const file = new File([blob], item.filename || `${item.id}.${ext}`, { type: blob.type || undefined });
      URL.revokeObjectURL(url);
      if (onFile) await onFile(file);
    } catch { toast.error("Couldn't load that gallery item"); }
    finally { setLoading(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={disabled || loading} data-testid={testid}
        className={className || "flex items-center justify-center gap-1.5 border-2 border-[#00F5D4] bg-black text-[#00F5D4] px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest active:translate-y-[1px] disabled:opacity-50"}>
        {loading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />} {label}
      </button>
      {open && <PickerSheet bucket={bucket} title={title} onClose={() => setOpen(false)} onPicked={pick} />}
    </>
  );
}
